/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { useClients } from '../../lib/queries/clinical.js';
import { useIntake } from '../../lib/queries/intake.js';
import { PatientDetailsForm } from '../intake/patient-details-form.js';

const SELECT_CLASS =
  'focus-ring mb-5 h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';

/**
 * Parent → Patient Details. The authenticated intake module: the parent fills
 * their child's eight-section assessment and submits it to the clinic.
 */
export function ParentPatientDetailsPage(): JSX.Element {
  const { t } = useTranslation();
  const clients = useClients();
  const children = clients.data ?? [];
  const [childId, setChildId] = useState<string | null>(null);

  const onPick = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setChildId(e.target.value);
  }, []);

  const activeChildId = childId ?? children[0]?.id;
  const intake = useIntake(activeChildId);

  return (
    <div className="p-8">
      <PageHeader
        title={t('parent.patientDetails.title')}
        subtitle={t('parent.patientDetails.subtitle')}
      />

      <QueryState
        isLoading={clients.isLoading}
        isError={clients.isError}
        isEmpty={children.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('parent.dashboard.empty')}
      >
        {children.length > 1 && (
          <select
            className={SELECT_CLASS}
            value={activeChildId ?? ''}
            onChange={onPick}
            aria-label={t('parent.selectChild')}
          >
            {children.map((c) => (
              <option key={c.id} value={c.id}>
                {c.fullName}
              </option>
            ))}
          </select>
        )}

        <QueryState
          isLoading={intake.isLoading}
          isError={intake.isError}
          isEmpty={false}
          loadingLabel={t('common.loading')}
          errorLabel={t('common.loadError')}
          emptyLabel={t('parent.dashboard.empty')}
        >
          {intake.data !== undefined && <PatientDetailsForm view={intake.data} />}
        </QueryState>
      </QueryState>
    </div>
  );
}
